import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const planningKey = (seizoenId: string) =>
  ["beheer", "planning", seizoenId] as const;
export const kandidatenKey = (dagId: string, functieId: string) =>
  ["beheer", "kandidaten", dagId, functieId] as const;

export type Planning = components["schemas"]["PlanningOverzichtDto"];
export type PlanningDag = components["schemas"]["PlanningDagDto"];
export type PlanningNiveau = components["schemas"]["PlanningNiveauDto"];
export type PlanningBaan = components["schemas"]["PlanningBaanDto"];
export type PlanningRegel = components["schemas"]["PlanningRegelDto"];
export type PlanningPloeg = components["schemas"]["PlanningPloegDto"];
export type Bezetting = components["schemas"]["PlanningBezettingDto"];
export type Waarschuwing = components["schemas"]["PlanningWaarschuwingDto"];
export type OngeplaatstePloeg = components["schemas"]["OngeplaatstePloegDto"];
export type VerspreidCategorie = components["schemas"]["VerspreidCategorieDto"];
export type Kandidaat = components["schemas"]["BezettingKandidaatDto"];
export type Promotieplaats = components["schemas"]["PlanningPromotieplaatsDto"];

/**
 * `BezettingDoel` reaches the client as a number, the way every enum in this schema does. The
 * two names live here so no component has to write a bare 1 or 2.
 */
export const doel = { baan: 1, dag: 2 } as const;

/**
 * `BezetteStarttijd`, the same way: what a pin does with the ploeg already standing on the
 * starttijd it names.
 */
export const bezet = { weigeren: 0, overschrijven: 1, ruilen: 2 } as const;

/**
 * The API answers RFC 7807, so a refusal carries a Dutch sentence the beheerder can act on -
 * "Om 09:35 staat Jeugdbrandweer Zeist op deze baan" rather than a 409.
 */
function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

/** True once every niveau of the seizoen is definitief, which is when the planner stops running. */
export const alleDefinitief = (planning: Pick<Planning, "niveaus">) =>
  planning.niveaus.length > 0 &&
  planning.niveaus.every((niveau) => niveau.isDefinitief);

/**
 * Polls continuously until every niveau is definitief: `draait` still reads false right after a
 * run is asked for, so gating the interval on it left a gap. `refetchIntervalInBackground`, or
 * an unfocused tab stops polling exactly when it matters.
 */
export function usePlanning(seizoenId: string | undefined) {
  return useQuery({
    queryKey: planningKey(seizoenId ?? ""),
    refetchInterval: (query) =>
      query.state.data && alleDefinitief(query.state.data) ? false : 2000,
    refetchIntervalInBackground: true,
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/planning",
            { params: { path: { seizoenId } } },
          );

          if (!data)
            throw problem(error, response, i18n.t("planning:errors.ophalen"));
          return data;
        },
  });
}

/**
 * Who offered this functie on this dag. Fetched when a dialog opens rather than with the
 * planning: a season's worth would be several hundred lists nobody looks at.
 */
export function useKandidaten(
  dagId: string | undefined,
  functieId: string | undefined,
) {
  return useQuery({
    queryKey: kandidatenKey(dagId ?? "", functieId ?? ""),
    queryFn:
      !dagId || !functieId
        ? skipToken
        : async () => {
            const { data, error, response } = await api.GET(
              "/api/beheer/wedstrijddagen/{dagId}/kandidaten/{functieId}",
              { params: { path: { dagId, functieId } } },
            );

            if (!data)
              throw problem(
                error,
                response,
                i18n.t("planning:errors.kandidaten"),
              );
            return data;
          },
  });
}

/**
 * Seeds the cache with what came back rather than refetching: a pin is not a local change, and a
 * refetch would show the state before the beheerder's own save until it lands.
 */
function useOverzichtMutation<TVariables>(
  seizoenId: string,
  send: (variables: TVariables) => Promise<Planning>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: send,
    onSuccess: (planning) =>
      queryClient.setQueryData(planningKey(seizoenId), planning),
  });
}

/**
 * The run is a job (ADR-0038), so the answer can predate `draait` turning true. The polling above
 * catches that, and `Stand` disables its own button on the click.
 */
export function useHerbereken(seizoenId: string) {
  return useOverzichtMutation(seizoenId, async () => {
    const { data, error, response } = await api.POST(
      "/api/beheer/seizoenen/{seizoenId}/planning/herbereken",
      { params: { path: { seizoenId } } },
    );

    if (!data)
      throw problem(error, response, i18n.t("planning:errors.herberekening"));
    return data;
  });
}

/**
 * §4.4.6: fixes one niveau's planning and publishes it. The engine stops running on that niveau
 * from here (ADR-0038, ADR-0081), and on the whole seizoen once every niveau is definitief.
 */
export function useDefinitiefMaken(seizoenId: string, niveauId: string) {
  return useOverzichtMutation(seizoenId, async () => {
    const { data, error, response } = await api.POST(
      "/api/beheer/seizoenen/{seizoenId}/planning/niveaus/{niveauId}/definitief",
      { params: { path: { seizoenId, niveauId } } },
    );

    if (!data)
      throw problem(error, response, i18n.t("planning:errors.definitiefMaken"));
    return data;
  });
}

/**
 * Takes it back off definitief. It queues no recompute - the beheerder asks for one with the
 * herbereken button, which this is what re-enables.
 */
export function useTerugzetten(seizoenId: string, niveauId: string) {
  return useOverzichtMutation(seizoenId, async () => {
    const { data, error, response } = await api.DELETE(
      "/api/beheer/seizoenen/{seizoenId}/planning/niveaus/{niveauId}/definitief",
      { params: { path: { seizoenId, niveauId } } },
    );

    if (!data)
      throw problem(error, response, i18n.t("planning:errors.terugzetten"));
    return data;
  });
}

export function useVastzettenIndeling(seizoenId: string) {
  return useOverzichtMutation(
    seizoenId,
    async (body: {
      ploegId: string;
      wedstrijdbaanId: string;
      speelschemaregelId: string;
      /** Optional: the API stores null where nothing was typed. */
      reden: string | null;
      /** One of `bezet`. Only read where another ploeg stands on the starttijd. */
      bezet: number;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/seizoenen/{seizoenId}/planning/indelingen",
        { params: { path: { seizoenId } }, body },
      );

      if (!data)
        throw problem(
          error,
          response,
          i18n.t("planning:errors.ploegVastzetten"),
        );
      return data;
    },
  );
}

export function useLosmakenIndeling(seizoenId: string) {
  return useOverzichtMutation(seizoenId, async (regelId: string) => {
    const { data, error, response } = await api.DELETE(
      "/api/beheer/seizoenen/{seizoenId}/planning/indelingen/{regelId}",
      { params: { path: { seizoenId, regelId } } },
    );

    if (!data)
      throw problem(error, response, i18n.t("planning:errors.ploegLosmaken"));
    return data;
  });
}

/**
 * Takes the ploeg off a starttijd altogether, which is not what losmaken does: that one hands the
 * row back to the planner and leaves the ploeg standing on it.
 */
export function useVerwijderenIndeling(seizoenId: string) {
  return useOverzichtMutation(seizoenId, async (regelId: string) => {
    const { data, error, response } = await api.DELETE(
      "/api/beheer/seizoenen/{seizoenId}/planning/indelingen/{regelId}/ploeg",
      { params: { path: { seizoenId, regelId } } },
    );

    if (!data)
      throw problem(
        error,
        response,
        i18n.t("planning:errors.ploegVerwijderen"),
      );
    return data;
  });
}

export function useVastzettenBezetting(seizoenId: string) {
  return useOverzichtMutation(
    seizoenId,
    async (body: {
      doel: number;
      doelId: string;
      functieId: string;
      persoonId: string;
      /** Optional: the API stores null where nothing was typed. */
      reden: string | null;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/seizoenen/{seizoenId}/planning/bezetting",
        { params: { path: { seizoenId } }, body },
      );

      if (!data)
        throw problem(
          error,
          response,
          i18n.t("planning:errors.functieVastzetten"),
        );
      return data;
    },
  );
}

export function useLosmakenBezetting(seizoenId: string) {
  return useOverzichtMutation(
    seizoenId,
    async (query: { doel: number; doelId: string; functieId: string }) => {
      const { data, error, response } = await api.DELETE(
        "/api/beheer/seizoenen/{seizoenId}/planning/bezetting",
        { params: { path: { seizoenId }, query } },
      );

      if (!data)
        throw problem(
          error,
          response,
          i18n.t("planning:errors.functieLosmaken"),
        );
      return data;
    },
  );
}

export function useMarkPromotie(seizoenId: string) {
  return useOverzichtMutation(
    seizoenId,
    async ({
      promotieplaatsId,
      ploegId,
    }: {
      promotieplaatsId: string;
      ploegId: string;
    }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/promotieplaatsen/{promotieplaatsId}/ploeg",
        { params: { path: { promotieplaatsId } }, body: { ploegId } },
      );

      if (!data)
        throw problem(error, response, i18n.t("planning:errors.promotieMark"));
      return data;
    },
  );
}

export function useUndoPromotie(seizoenId: string) {
  return useOverzichtMutation(seizoenId, async (promotieplaatsId: string) => {
    const { data, error, response } = await api.DELETE(
      "/api/beheer/promotieplaatsen/{promotieplaatsId}/ploeg",
      { params: { path: { promotieplaatsId } } },
    );

    if (!data)
      throw problem(error, response, i18n.t("planning:errors.promotieUndo"));
    return data;
  });
}

/**
 * The warnings that point at one cell. Grouping happens here rather than on the server because
 * the server answers per wedstrijddag and the grid asks per row and per functie.
 */
export function waarschuwingenVoorRegel(dag: PlanningDag, regelId: string) {
  return dag.waarschuwingen.filter((w) => w.speelschemaregelId === regelId);
}

export function waarschuwingenVoorFunctie(
  dag: PlanningDag,
  baanId: string | null,
  functieId: string,
) {
  return dag.waarschuwingen.filter(
    (w) =>
      w.functieId === functieId &&
      (w.wedstrijdbaanId ?? null) === baanId &&
      w.speelschemaregelId === null,
  );
}
